import React, { useState } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { getPteReadingExplanation } from "../../Client/request";
import { buildPteReadingExplanationPayload } from "./pteReadingExplanationUtils";
import { parsePteReadingExplanation } from "./pteReadingExplanationDisplayUtils";

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 1300,
  background: "rgba(24, 18, 43, 0.34)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "1rem",
};

const modalStyle = {
  width: "min(620px, 100%)",
  background: "#fff",
  borderRadius: "12px",
  boxShadow: "0 24px 70px rgba(24, 18, 43, 0.25)",
  padding: "1.25rem",
  color: "#2f2643",
  fontFamily: "Noto Sans, sans-serif",
  fontSize: "15px",
  fontWeight: 400,
  lineHeight: 1.5,
  letterSpacing: "normal",
  wordSpacing: "normal",
  textAlign: "left",
};

const explanationListStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "0.65rem",
};

const explanationBlockStyle = {
  border: "1px solid rgba(106, 65, 203, 0.12)",
  background: "rgba(106, 65, 203, 0.045)",
  borderRadius: "10px",
  padding: "0.7rem 0.8rem",
};

const explanationBulletStyle = {
  display: "grid",
  gridTemplateColumns: "24px 1fr",
  gap: "0.55rem",
  alignItems: "start",
  padding: "0.15rem 0.1rem",
};

const aiLabelStyle = {
  display: "inline-flex",
  alignItems: "center",
  alignSelf: "flex-start",
  gap: "0.35rem",
  padding: "0.22rem 0.55rem",
  borderRadius: "999px",
  background: "rgba(106, 65, 203, 0.1)",
  color: "#6a41cb",
  fontSize: "0.78rem",
  fontWeight: 800,
  lineHeight: 1.2,
  textTransform: "uppercase",
  letterSpacing: "0.02em",
};

const iconButtonStyle = {
  width: "30px",
  height: "30px",
  borderRadius: "50%",
  border: "1px solid rgba(106, 65, 203, 0.22)",
  background: "rgba(106, 65, 203, 0.08)",
  color: "#6a41cb",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  flex: "0 0 auto",
};

const headerIconButtonStyle = {
  ...iconButtonStyle,
  border: "1px solid rgba(255, 255, 255, 0.7)",
  background: "#fff",
  color: "#6a41cb",
  boxShadow: "0 4px 14px rgba(24, 18, 43, 0.18)",
};

const actionButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "0.5rem",
  minHeight: "40px",
  padding: "0.68rem 1.1rem",
  borderRadius: "0.25rem",
  border: "1px solid var(--Brand-Purple, #996CFE)",
  background: "none",
  color: "#996CFE",
  fontFamily: "Noto Sans, sans-serif",
  fontSize: "1rem",
  fontWeight: 500,
  lineHeight: "1.125rem",
  cursor: "pointer",
};

const PteReadingExplanationButton = ({ explanationContext, testData, target, variant = "body" }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [explanation, setExplanation] = useState("");
  const [error, setError] = useState("");

  const payload = buildPteReadingExplanationPayload({
    explanationContext,
    testData,
    target,
  });

  const fetchExplanation = async () => {
    if (explanation || loading) return;

    if (!payload) {
      setError("Explanation is not available for this saved result yet.");
      return;
    }

    setLoading(true);
    setError("");
    const result = await getPteReadingExplanation(payload);
    if (result.error) {
      setError(result.error);
    } else {
      setExplanation(result.data?.explanation || "");
    }
    setLoading(false);
  };

  const handleClick = async (event) => {
    event.stopPropagation();
    setOpen(true);
    await fetchExplanation();
  };

  const renderExplanation = () => {
    const blocks = parsePteReadingExplanation(explanation);
    if (!blocks.length) return null;

    return (
      <div style={explanationListStyle}>
        {blocks.map((block, index) => {
          if (block.type === "heading") {
            return (
              <div
                key={`${block.type}-${index}`}
                style={{
                  fontWeight: 800,
                  color: "#2c2147",
                  marginTop: index === 0 ? 0 : "0.2rem",
                }}
              >
                {block.label}
              </div>
            );
          }

          if (block.type === "label") {
            return (
              <div key={`${block.type}-${index}`} style={explanationBlockStyle}>
                <div style={{ fontSize: "0.78rem", fontWeight: 800, color: "#6a41cb", textTransform: "uppercase" }}>
                  {block.label}
                </div>
                <div style={{ marginTop: "0.25rem", color: "#332a45" }}>{block.text}</div>
              </div>
            );
          }

          if (block.type === "bullet" || block.type === "number") {
            return (
              <div key={`${block.type}-${index}`} style={explanationBulletStyle}>
                <span
                  style={{
                    width: "24px",
                    height: "24px",
                    borderRadius: "50%",
                    background: block.type === "number" ? "#6a41cb" : "rgba(106, 65, 203, 0.12)",
                    color: block.type === "number" ? "#fff" : "#6a41cb",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.8rem",
                    fontWeight: 800,
                  }}
                >
                  {block.type === "number" ? block.number : "•"}
                </span>
                <span>{block.text}</span>
              </div>
            );
          }

          return <p key={`${block.type}-${index}`} style={{ margin: 0 }}>{block.text}</p>;
        })}
      </div>
    );
  };

  return (
    <>
      <button
        type="button"
        title={target?.label || "Explain answer"}
        aria-label={target?.label || "Explain answer"}
        onClick={handleClick}
        style={
          variant === "header"
            ? headerIconButtonStyle
            : variant === "action"
            ? actionButtonStyle
            : iconButtonStyle
        }
      >
        {variant === "action" && <span>AI Explanation</span>}
        <InfoOutlinedIcon style={{ fontSize: "18px" }} />
      </button>

      {open && (
        <div style={overlayStyle} onMouseDown={(event) => event.stopPropagation()}>
          <div style={modalStyle}>
            <div style={aiLabelStyle}>AI Explanation</div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "1rem",
                alignItems: "center",
                marginTop: "0.75rem",
                marginBottom: "0.85rem",
              }}
            >
              <strong style={{ fontSize: "1.05rem" }}>{target?.label || "Answer explanation"}</strong>
              <button
                type="button"
                onClick={() => setOpen(false)}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#6f6680",
                  cursor: "pointer",
                  fontSize: "1.2rem",
                  lineHeight: 1,
                }}
              >
                x
              </button>
            </div>
            <div style={{ fontSize: "0.98rem", lineHeight: 1.6, color: "#3b334d" }}>
              {loading && "Preparing explanation..."}
              {!loading && error && (
                <span style={{ color: "#c62828" }}>{error}</span>
              )}
              {!loading && !error && renderExplanation()}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PteReadingExplanationButton;
